import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useInstagram } from '../context/InstagramContext';
import { useFacebook } from '../context/FacebookContext';
import AddAccountModal from '../components/AddAccountModal';

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';
const FACEBOOK_REDIRECT_URI = import.meta.env.VITE_FACEBOOK_REDIRECT_URI;

const MetaCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const instagram = useInstagram();
  const facebook = useFacebook();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [tokenRef, setTokenRef] = useState(null);
  const [platform, setPlatform] = useState(null); // 'instagram' or 'facebook'
  const [alreadyConnected, setAlreadyConnected] = useState(false);
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (!isAuthenticated) {
        setError('Please log in first to connect your account');
        setStatus('error');
        return;
      }

      if (errorParam) {
        setError(searchParams.get('error_description') || 'Authorization was denied');
        setStatus('error');
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setStatus('error');
        return;
      }

      // Check if this is an Instagram comments upgrade callback
      if (state?.startsWith('igupgrade_')) {
        setPlatform('instagram');

        // On tunnel redirect, sessionStorage was already cleared by the first load.
        // Extract accountId directly from state: igupgrade_{accountId}_{random}
        const parts = state.split('_');
        const accountId = parts[1]; // e.g. "5771708830"

        try {
          const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/upgrade-comments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              code,
              redirect_uri: FACEBOOK_REDIRECT_URI,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to upgrade comments access');
          }

          const result = await response.json();

          // Update accounts in context
          await instagram.loadConnectedAccounts();

          // A 200 here does not mean the metrics were unlocked: without a
          // linked Facebook Page the backend still enables comments and says
          // so explicitly. Show that rather than a success screen that
          // contradicts the still-empty charts.
          if (result.metrics_access === 'limited') {
            setError(result.message || 'Connected, but no linked Facebook Page was found.');
            setStatus('partial');
            return;
          }

          setStatus('success');
          setTimeout(() => navigate(`/ig-account/${accountId}`), 1500);
        } catch (err) {
          setError(err.message);
          setStatus('error');
        }
        return;
      }

      // Determine platform from state prefix
      const detectedPlatform = state?.startsWith('fb_') ? 'facebook' : 'instagram';
      setPlatform(detectedPlatform);

      const context = detectedPlatform === 'facebook' ? facebook : instagram;

      try {
        const result = await context.handleCallback(code, state);

        if (result.availableAccounts.length === 1) {
          setStatus('connecting');
          const sel = await context.selectAccount(result.tokenRef, result.availableAccounts[0].page_id);
          const wasAlready = !!sel?.alreadyConnected;
          setAlreadyConnected(wasAlready);
          setStatus('success');
          // Only auto-redirect for a genuinely new connection; if it was the same
          // account, stay so the "log out to add a different one" modal is seen.
          if (!wasAlready) {
            setTimeout(() => navigate('/dashboard'), 1500);
          }
        } else {
          setAvailableAccounts(result.availableAccounts);
          setTokenRef(result.tokenRef);
          setStatus('select');
        }
      } catch (err) {
        setError(err.message);
        setStatus('error');
      }
    };

    processCallback();
  }, [searchParams, instagram, facebook, navigate, isAuthenticated]);

  const handleSelectAccount = async (pageId) => {
    try {
      setStatus('connecting');
      const context = platform === 'facebook' ? facebook : instagram;
      const sel = await context.selectAccount(tokenRef, pageId);
      const wasAlready = !!sel?.alreadyConnected;
      setAlreadyConnected(wasAlready);
      setStatus('success');
      if (!wasAlready) {
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  const platformLabel = platform === 'facebook' ? 'Facebook Page' : 'Instagram';
  const platformBadgeClass = platform === 'facebook'
    ? 'bg-brand-facebook text-white'
    : 'bg-accent text-accent-ink';
  const hoverBorderColor = platform === 'facebook' ? 'hover:border-brand-facebook' : 'hover:border-brand-instagram';

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="max-w-lg w-full text-center">
        {status === 'processing' && (
          <>
            <div className="text-6xl mb-4 animate-pulse">🔄</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Connecting {platformLabel || 'account'}...</h1>
            <p className="text-ink-muted">Please wait while we discover your accounts</p>
          </>
        )}

        {status === 'connecting' && (
          <>
            <div className="text-6xl mb-4 animate-pulse">🔄</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Setting up account...</h1>
            <p className="text-ink-muted">Almost there</p>
          </>
        )}

        {status === 'select' && (
          <>
            <h1 className="text-2xl font-bold text-ink mb-2">Select {platform === 'facebook' ? 'a Page' : 'an Account'}</h1>
            <p className="text-ink-muted mb-6">
              We found {availableAccounts.length} {platform === 'facebook' ? 'Facebook Pages' : 'Instagram Business accounts'}. Choose which one to connect:
            </p>
            <div className="space-y-3">
              {availableAccounts.map((acct) => {
                const name = platform === 'facebook'
                  ? acct.page_name
                  : acct.ig_username;
                const subtitle = platform === 'facebook'
                  ? `${acct.page_category || 'Page'} · ${(acct.followers_count || 0).toLocaleString()} followers`
                  : `${acct.page_name} · ${(acct.ig_followers_count || 0).toLocaleString()} followers`;
                const imageUrl = platform === 'facebook'
                  ? acct.profile_picture_url
                  : acct.ig_profile_picture_url;
                const displayChar = (name || '?')[0].toUpperCase();

                return (
                  <button
                    key={acct.page_id}
                    onClick={() => handleSelectAccount(acct.page_id)}
                    className={`w-full flex items-center gap-4 p-4 bg-surface rounded-xl border border-line ${hoverBorderColor} hover:shadow-md transition text-left`}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-full ${platform === 'facebook' ? 'bg-brand-facebook' : 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#515BD4]'} flex items-center justify-center text-white font-bold text-lg`}>
                        {displayChar}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-ink truncate">
                        {platform === 'facebook' ? name : `@${name || 'Unknown'}`}
                      </div>
                      <div className="text-sm text-ink-muted truncate">
                        {subtitle}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => navigate('/connect')}
              className="mt-4 text-sm text-ink-muted hover:text-ink transition"
            >
              Cancel
            </button>
          </>
        )}

        {status === 'success' && !alreadyConnected && (
          <>
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Connected!</h1>
            <p className="text-ink-muted">Your {platformLabel} has been connected successfully</p>
            <p className="text-sm text-ink-muted mt-4">Redirecting to dashboard...</p>
          </>
        )}

        {status === 'success' && alreadyConnected && (
          <>
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Done</h1>
            <p className="text-ink-muted">Your {platformLabel} is connected.</p>
            <AddAccountModal
              open={true}
              platform={platform}
              variant="already"
              onRetry={platform === 'facebook' ? facebook.connectFacebook : instagram.connectInstagram}
              onClose={() => navigate('/dashboard')}
            />
          </>
        )}

        {/* The Facebook connection worked; there was just no Page to bind, so
            the metrics stay limited. Calling that a failure would be wrong --
            comments access really was granted -- and calling it a success
            would contradict the empty charts they are about to see. */}
        {status === 'partial' && (
          <>
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Partly connected</h1>
            <p className="text-warning mb-6">{error}</p>
            <div className="text-left text-sm text-ink-muted bg-warning-tint border border-warning rounded-lg p-4 mb-6">
              <p className="font-medium text-amber-900 mb-2">To unlock per-post metrics:</p>
              <ol className="list-decimal ml-5 space-y-1">
                <li>Make the Instagram account a Business or Creator account.</li>
                <li>Link it to a Facebook Page you manage.</li>
                <li>Run this upgrade again.</li>
              </ol>
            </div>
            <button
              onClick={() => navigate('/connect')}
              className="bg-accent text-accent-ink px-6 py-3 rounded-lg font-medium hover:bg-accent-hover transition min-h-[var(--tap-target)] sm:min-h-0"
            >
              Back to accounts
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Connection Failed</h1>
            <p className="text-danger mb-6">{error}</p>
            <button
              onClick={() => navigate('/connect')}
              className="bg-accent text-accent-ink px-6 py-3 rounded-lg font-medium hover:bg-accent-hover transition min-h-[var(--tap-target)] sm:min-h-0"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MetaCallback;
